import LocationOnIcon from "@mui/icons-material/LocationOn";
import OpenInNew from "@mui/icons-material/OpenInNew";
import { styled } from "@mui/material";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { CustomLink } from "core/components/CustomLink";
import { useFormatDate } from "core/hooks/useFormatDate";
import { useIntlInstance } from "core/hooks/useIntlInstance";
import { useTranslation } from "react-i18next";

type EventWidgetTileProps = {
  id: string;
  title: string;
  startDate: string | Date | null | undefined;
  address?: string | null | undefined;
  url?: string | null | undefined;
};

const CONTAINER_NAME = "date-box";

function DateBox({ day, month }: { day: number; month: string }) {
  const intlInstance = useIntlInstance();
  const order: ["month", "day"] | ["day", "month"] = intlInstance
    .formatToParts(new Date())
    .map((part) => part.type)
    .filter((type) => ["month", "day"].includes(type)) as any;
  return (
    <Stack
      paddingY={2}
      paddingX={3}
      marginX="auto"
      sx={{
        aspectRatio: "1 / 1",
        containerType: "inline-size", // required for container queries
        containerName: CONTAINER_NAME,
      }}
      maxWidth="175px"
      borderRadius="5px"
      bgcolor="primary.main"
      color="primary.contrastText"
      textAlign="center"
      alignItems="center"
      justifyContent="center"
      gap={1}
    >
      {order.map((part) => {
        if (part === "month") {
          return (
            <Typography key="month" variant="h4" lineHeight={1}>
              {month}
            </Typography>
          );
        } else {
          return (
            <Typography key="day" variant="h4" lineHeight={1}>
              {day}
            </Typography>
          );
        }
      })}
    </Stack>
  );
}

const TileContainer = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: theme.spacing(2),
  [theme.containerQueries(CONTAINER_NAME).up(275)]: {
    gridTemplateColumns: "minmax(auto,200px) 1fr",
  },
}));

export function EventsWidgetTile({
  id,
  title,
  startDate,
  address,
  url,
}: EventWidgetTileProps) {
  const { t } = useTranslation("NewsEvents", {
    keyPrefix: "Next:NewsEvents:EventsWidgetTile",
  });
  const getMonth = useFormatDate({ month: "short" });
  if (!startDate) {
    return null;
  }
  const month = getMonth(startDate);
  const day = new Date(startDate).getDate();
  const href = `/events/${id}`;
  return (
    <TileContainer>
      <CustomLink href={href}>
        <DateBox day={day} month={month} />
      </CustomLink>
      <Stack flex={1} minWidth="125px" alignItems="center">
        <CustomLink href={href} textAlign="center">
          <Typography variant="body1">{title}</Typography>
        </CustomLink>
        {address && (
          <CustomLink
            textAlign="center"
            target="_blank"
            href={`https://www.google.com/maps/search/${encodeURIComponent(
              address
            )}`}
          >
            <Typography color="text.secondary">
              <LocationOnIcon color="primary" /> {address}
            </Typography>
          </CustomLink>
        )}
        {url && (
          <CustomLink
            href={url}
            target="_blank"
            maxWidth="100%"
            overflow="hidden"
            textOverflow="ellipsis"
            color="text.secondary"
          >
            <OpenInNew color="primary" /> {url}
          </CustomLink>
        )}
        <CustomLink href={href} sx={{ mt: 2 }}>
          {t("details")}
        </CustomLink>
      </Stack>
    </TileContainer>
  );
}
